import { useApolloClient } from "@apollo/client";
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ROUTE_NAMES } from "../../app/constants/TextConstants";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { API_SLICES } from "../../redux/store";
import {
  TwoStepCheck,
  TwoStepPass,
  useCheckTwoStepMutation,
} from "../../slices/TwoStepSlice";
import { refetchRefused } from "./refetchRefused";
import { recheckDelay, TWO_STEP_REQUIRED_EVENT } from "./twoStep.utils";
import {
  TwoStepCodeScreen,
  TwoStepRequiredSetupScreen,
} from "./TwoStepCodeScreen";
import { TwoStepSetup } from "./TwoStepSetup";
import {
  clearProof,
  deviceTokenForEmail,
  saveDeviceToken,
  savedProofExpiry,
  saveProof,
} from "./twoStepStorage";

type GateState = "checking" | "open" | "code" | "setup";

/**
 * Two-step sign-in (Roadmap 1845): runs right after the Microsoft sign-in,
 * before the app loads the person's data. Asks the API whether a code is
 * needed (`api/two-factor/check`, with this computer's 30-day token if any):
 * - off / passed -> the app as usual (people without two-step see no change);
 * - code -> the code screen; setup -> set it up (the firm requires it).
 * If the API later answers "two_factor_required" (proof ran out, the firm
 * switched it on, an admin reset it), the screen comes back on top of the app.
 * If the check itself fails (older server without two-step), the app opens:
 * the API is what enforces it.
 */
export const TwoStepGate: FC<{ email: string; children: ReactNode }> = ({
  email,
  children,
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState<GateState>("checking");
  const [check, setCheck] = useState<TwoStepCheck | null>(null);
  const [opened, setOpened] = useState(false);
  const [runCheck] = useCheckTwoStepMutation();
  const running = useRef(false);
  const store = useStore();
  const apollo = useApolloClient();
  // The API refused calls of the open app (403 two_factor_required): once the
  // code is entered (or a remembered computer passes silently) those calls
  // are made again, so the page behind fills in (QA batch 18, 2FA-036).
  const refused = useRef(false);

  const reopen = useCallback(() => {
    setState("open");
    if (!refused.current) return;
    refused.current = false;
    // After React has re-rendered without the code screen.
    window.setTimeout(() => {
      refetchRefused(
        store as unknown as Parameters<typeof refetchRefused>[0],
        API_SLICES as unknown as Parameters<typeof refetchRefused>[1],
        apollo,
      );
    }, 0);
  }, [store, apollo]);

  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      const result = await runCheck({
        deviceToken: deviceTokenForEmail(email),
      });
      if ("error" in result) {
        reopen();
        return;
      }
      const data = result.data;
      if (data.deviceTokenInvalid) saveDeviceToken(email, null);
      if (data.proof && data.proofExpiresAt)
        saveProof(email, data.proof, data.proofExpiresAt);
      setCheck(data);
      if (data.state === "code") setState("code");
      else if (data.state === "setup") setState("setup");
      else reopen();
    } finally {
      running.current = false;
    }
  }, [email, runCheck, reopen]);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    if (state === "open") setOpened(true);
  }, [state]);

  // The API refused a call: the proof is gone (or never was). Check again.
  useEffect(() => {
    const onRequired = () => {
      refused.current = true;
      clearProof();
      void run();
    };
    window.addEventListener(TWO_STEP_REQUIRED_EVENT, onRequired);
    return () =>
      window.removeEventListener(TWO_STEP_REQUIRED_EVENT, onRequired);
  }, [run]);

  // Shortly before the 12-hour proof runs out, check again: a remembered
  // computer gets a new one silently; otherwise the code screen shows.
  useEffect(() => {
    if (state !== "open") return;
    const delay = recheckDelay(savedProofExpiry());
    if (delay === null || delay > 2_000_000_000) return;
    const timer = window.setTimeout(() => {
      clearProof();
      void run();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state, run, check]);

  const passed = (pass: TwoStepPass, remembered = false) => {
    saveProof(email, pass.proof, pass.proofExpiresAt);
    if (remembered && pass.deviceToken)
      saveDeviceToken(email, pass.deviceToken, pass.deviceExpiresAt);
    reopen();
  };

  const signOut = () => {
    clearProof();
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }
    navigate(ROUTE_NAMES.signOut);
  };

  const showApp = opened || state === "open";
  if (state === "checking" && !showApp) return <LottieLoading />;

  const screen =
    state === "code" && check ? (
      <TwoStepCodeScreen check={check} onPassed={passed} onSignOut={signOut} />
    ) : state === "setup" && check ? (
      <TwoStepRequiredSetupScreen
        check={check}
        onSignOut={signOut}
        setup={
          <TwoStepSetup email={email} onDone={(pass) => passed(pass, false)} />
        }
      />
    ) : null;

  // Before the app has loaded: only the screen. Later: on top of the app, so
  // nothing typed is lost.
  if (!showApp) return screen;
  return (
    <>
      {children}
      {screen}
    </>
  );
};
